import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import type { MentionSuggestion } from '@/lib/schemas/mention';

import {
  activeMentionQuery,
  insertMention,
  mentionRefs,
  toDisplayText,
  toMarkup,
  type ActiveQuery,
  type MentionRef,
} from './mention-token';
import { useMentionSuggestions } from './useMentionSuggestions';

type MentionInputProps = Omit<TextFieldProps, 'value' | 'onChange' | 'select'> & {
  value: string;
  onChange: (value: string) => void;
};

/**
 * A text field where typing `@` offers people to tag.
 *
 * The field shows `@Alina Berce`; the value handed to the caller is
 * `@[Alina Berce](user:cuid)`. Those are two different things on purpose — the
 * stored form is how the link survives a rename, and it has no business being
 * in front of somebody writing a sentence.
 *
 * Implemented as an ARIA combobox over a listbox rather than a bare popup: the
 * field keeps focus throughout, and `aria-activedescendant` tells a screen
 * reader which option is current while the arrow keys move through them. The
 * mouse and the keyboard reach exactly the same options.
 */
export function MentionInput({ value, onChange, ...fieldProps }: MentionInputProps) {
  const { t } = useTranslation();
  const listboxId = useId();
  const optionId = useId();

  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  /**
   * What the field shows, and the mentions behind it.
   *
   * Seeded from the incoming value — editing an existing post starts with its
   * tags already placed — and resynced only when the value changes from
   * outside, so a form reset lands but the caller echoing our own emission does
   * not rewrite the text under the caret.
   */
  const [display, setDisplay] = useState(() => toDisplayText(value));
  const [refs, setRefs] = useState<MentionRef[]>(() => mentionRefs(value));
  const emitted = useRef(value);

  useEffect(() => {
    if (value === emitted.current) return;
    emitted.current = value;
    setDisplay(toDisplayText(value));
    setRefs(mentionRefs(value));
  }, [value]);

  const emit = (nextDisplay: string, nextRefs: readonly MentionRef[]): void => {
    const markup = toMarkup(nextDisplay, nextRefs);
    emitted.current = markup;
    onChange(markup);
  };

  const [active, setActive] = useState<ActiveQuery | null>(null);
  const [highlighted, setHighlighted] = useState(0);
  /**
   * The query Escape dismissed.
   *
   * Without it, closing the list and then releasing the same key reopens it:
   * the keyup handler recomputes the `@…` under the caret, which has not
   * changed. Typing another character moves past this and offers again.
   */
  const dismissed = useRef<string | null>(null);

  const { suggestions } = useMentionSuggestions(active?.query ?? null);
  const isOpen = active !== null && suggestions.length > 0;

  useEffect(() => {
    setHighlighted(0);
  }, [active?.query]);

  const syncActive = (element: HTMLTextAreaElement | HTMLInputElement): void => {
    const next = activeMentionQuery(element.value, element.selectionStart ?? element.value.length);

    if (next && dismissed.current === next.query) {
      setActive(null);
      return;
    }

    dismissed.current = null;
    setActive(next);
  };

  const choose = (suggestion: MentionSuggestion): void => {
    if (!active) return;

    const next = insertMention(display, active, suggestion);
    const nextRefs = [...refs, next.ref];

    setDisplay(next.display);
    setRefs(nextRefs);
    emit(next.display, nextRefs);
    setActive(null);

    // Put the caret after the token that was just inserted, so typing carries
    // on where the writer left off rather than at the end of the field.
    requestAnimationFrame(() => {
      const element = inputRef.current;
      if (!element) return;
      element.focus();
      element.setSelectionRange(next.caret, next.caret);
    });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (!isOpen) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlighted((current) => (current + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted((current) => (current - 1 + suggestions.length) % suggestions.length);
    } else if (event.key === 'Enter' || event.key === 'Tab') {
      const suggestion = suggestions[highlighted];
      if (suggestion) {
        // Enter would otherwise submit the form or break the line.
        event.preventDefault();
        choose(suggestion);
      }
    } else if (event.key === 'Escape') {
      // Closes the list without touching what has been typed — Escape a second
      // time is the dialog's to handle.
      event.stopPropagation();
      dismissed.current = active.query;
      setActive(null);
    }
  };

  return (
    <ClickAwayListener onClickAway={() => setActive(null)}>
      <Box ref={anchorRef}>
        <TextField
          {...fieldProps}
          value={display}
          inputRef={(element: HTMLTextAreaElement | HTMLInputElement | null) => {
            inputRef.current = element;
          }}
          onChange={(event) => {
            setDisplay(event.target.value);
            emit(event.target.value, refs);
            syncActive(event.target);
          }}
          onKeyUp={(event) => syncActive(event.target as HTMLTextAreaElement)}
          onClick={(event) => syncActive(event.target as HTMLTextAreaElement)}
          onKeyDown={handleKeyDown}
          onBlur={(event) => {
            fieldProps.onBlur?.(event);
            setActive(null);
          }}
          inputProps={{
            ...fieldProps.inputProps,
            role: 'combobox',
            'aria-expanded': isOpen,
            'aria-controls': isOpen ? listboxId : undefined,
            'aria-autocomplete': 'list',
            'aria-activedescendant': isOpen ? `${optionId}-${highlighted}` : undefined,
          }}
        />

        <Popper
          open={isOpen}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1400, width: anchorRef.current?.clientWidth }}
        >
          <Paper variant="outlined" sx={{ mt: 0.5, overflow: 'hidden' }}>
            <Box component="ul" id={listboxId} role="listbox" aria-label={t('mentions.listLabel')}>
              {suggestions.map((suggestion, index) => (
                <Box
                  component="li"
                  key={`${suggestion.type}:${suggestion.id}`}
                  id={`${optionId}-${index}`}
                  role="option"
                  aria-selected={index === highlighted}
                  onMouseDown={(event) => {
                    // Before blur, or the field would close the list first.
                    event.preventDefault();
                    choose(suggestion);
                  }}
                  onMouseEnter={() => setHighlighted(index)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 1.5,
                    py: 1,
                    cursor: 'pointer',
                    bgcolor: index === highlighted ? 'action.hover' : 'transparent',
                  }}
                >
                  <Avatar src={suggestion.avatarUrl ?? undefined} sx={{ width: 32, height: 32 }}>
                    {suggestion.displayName.charAt(0)}
                  </Avatar>
                  <Stack sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" noWrap>
                      {suggestion.displayName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      @{suggestion.username}
                      {suggestion.town ? ` · ${suggestion.town}` : ''}
                    </Typography>
                  </Stack>
                </Box>
              ))}
            </Box>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}
